import React, { useCallback } from 'react';
import { useFileExplorerContext } from '../../hooks/useFileExplorerContext';
import { useActiveFolderData } from '../../hooks/useActiveFolderData';
import { FileExplorerGridItem } from './FileExplorerGridItem';
import { FolderGridItemActions, FileGridItemActions } from './FileExplorerGridItemActions';
import { FileExplorerDropzone } from './FileExplorerDropzone';
import { TreeFolder } from './services';
import { LoadingSpinner } from '../common';

export function FileExplorerGridContent() {
  const {
    activeFolder, newlyAddedItems, setActiveFolder, expandFolders,
  } = useFileExplorerContext();
  const { data, isLoading } = useActiveFolderData();

  const handleFolderDoubleClick = useCallback((folder: TreeFolder) => {
    if (!activeFolder) return;

    const newPath = [...activeFolder.path, { id: folder.id, name: folder.name }];

    const ancestorIds = newPath.map((node) => node.id);
    expandFolders(ancestorIds);

    setActiveFolder({
      id: folder.id,
      path: newPath,
      metadata: {
        id: folder.id,
        name: folder.name,
        permissions: folder.permissions,
        shareable_link: folder.shareable_link,
      },
    });
  }, [activeFolder, setActiveFolder, expandFolders]);

  if (isLoading) {
    return (
      <div className="file-explorer-grid p-4 z-1 d-flex align-items-center justify-content-center h-100">
        <LoadingSpinner size={48} borderWidth={4} color="info" />
      </div>
    );
  }

  if (!data?.data || !activeFolder) return null;

  const { folders, files } = data.data;
  return (
    <div className="file-explorer-grid p-4 z-1">
      {folders?.map((folder) => (
        <FileExplorerGridItem
          key={folder.id}
          nodeTitle={folder.name}
          kind="folder"
          actionsDropdown={<FolderGridItemActions node={folder} />}
          isNewlyAdded={newlyAddedItems.has(folder.id)}
          onDoubleClick={() => handleFolderDoubleClick(folder)}
        />
      ))}
      {files?.map((file) => (
        <FileExplorerGridItem
          key={file.id}
          nodeTitle={file.title}
          updatedAt={file.updated_at}
          kind="file"
          isNewlyAdded={newlyAddedItems.has(file.id)}
          actionsDropdown={(
            <FileGridItemActions
              node={file}
              permissions={activeFolder.metadata.permissions}
              shareableLink={activeFolder.metadata.shareable_link}
            />
          )}
        />
      ))}
    </div>
  );
}

function FileExplorerGridEmptyState() {
  return (
    <div className="h-100 w-100 d-flex flex-column align-items-center justify-content-center gap-2">
      <img src="/static/img/icons/folder.svg" alt="Folder" height={64} />
      <span className="fs-6 fw-semibold">Selecciona una carpeta</span>
      <p className="w-50 text-center text-muted">
        Selecciona una carpeta para interactuar con su contenido.
      </p>
    </div>
  );
}

export function FileExplorerGrid() {
  const { activeFolder } = useFileExplorerContext();

  return (
    <FileExplorerDropzone>
      <div className="file-explorer-grid-container">
        {activeFolder ? (
          <FileExplorerGridContent />
        ) : (
          <FileExplorerGridEmptyState />
        )}
      </div>
    </FileExplorerDropzone>
  );
}
